/**
 * Angebots-Detail.
 *
 * Zweispaltig: links Status, Beteiligte, Mail-Verlauf, Gesprächsnotizen und
 * Statusverlauf – rechts der Datensatz und die weiteren Angebote des Kunden.
 *
 * Zwei Warnbanner sind bewusst dauerhaft, nicht flüchtig:
 *  - fehlgeschlagener Sync zu Monday/SharePoint
 *  - in Monday pausiert, aber ohne Wiedervorlage-Datum
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { AuftragModal } from '../modals/AuftragModal';
import { VersendetModal } from '../modals/VersendetModal';
import { GewerkModal } from '../modals/GewerkModal';
import { AnrufModal } from '../modals/AnrufModal';
import { MailVorschauModal } from '../modals/MailVorschauModal';
import { betragFormatieren, betragLesen, dat, eur, gruppiere } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';
import './detail.css';


export function AngebotDetail() {
  const { id } = useParams();
  const angebotId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const meta = useMeta();

  const [auftragOffen, setAuftragOffen] = useState(false);
  const [versendetOffen, setVersendetOffen] = useState(false);
  const [gewerkOffen, setGewerkOffen] = useState(false);
  const [anrufOffen, setAnrufOffen] = useState(false);
  const [mailOffen, setMailOffen] = useState(false);
  /** Aufgeklappte Statuskorrektur – bewusst versteckt, damit sie selten benutzt wird. */
  const [korrekturOffen, setKorrekturOffen] = useState(false);
  const [kalkWechsel, setKalkWechsel] = useState(false);
  const [auftragFeld, setAuftragFeld] = useState<string | null>(null);

  const abfrage = useQuery({
    queryKey: ['angebot', angebotId],
    queryFn: () => api.hole<Angebot>(`/angebote/${angebotId}`),
    enabled: Number.isFinite(angebotId),
  });

  const alleAbfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  function aktualisieren() {
    void queryClient.invalidateQueries({ queryKey: ['angebot', angebotId] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  }

  /**
   * Zuständigkeit wechseln. Schreibt auch die Personenspalte in Monday –
   * dort schaut man ja gerade deshalb hin.
   */
  const kalkulantMutation = useMutation({
    mutationFn: (kalkulant: string) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/kalkulant`, { kalkulant }),
    onSuccess: () => {
      void queryClient.invalidateQueries();
      setKalkWechsel(false);
      toastZeigen('Zuständigkeit gewechselt – Monday ist nachgezogen.');
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Wechsel fehlgeschlagen.'),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) =>
      api.sende<AngebotAntwort>(`/angebote/${angebotId}/status`, { status }),
    onSuccess: (a) => {
      aktualisieren();
      if (a.sync.fehler) toastZeigen('Status gespeichert – der Abgleich meldet ein Problem.');
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => api.sende<AngebotAntwort>(`/angebote/${angebotId}/sync`),
    onSuccess: (a) => {
      aktualisieren();
      if (a.toast) toastZeigen(a.toast);
    },
  });

  const wertMutation = useMutation({
    mutationFn: (auftrag: number | null) =>
      api.aendere<Angebot>(`/angebote/${angebotId}/auftragswert`, { auftrag }),
    onSuccess: aktualisieren,
  });



  const a = abfrage.data;

  const geschwister = useMemo(
    () => (alleAbfrage.data ?? []).filter((x) => a && x.kunde.id === a.kunde.id && x.id !== a.id),
    [alleAbfrage.data, a],
  );

  const verlauf = useMemo(() => {
    if (!a) return [];
    const absteigend = [...a.verlauf].reverse();
    return absteigend.map((h, i) => {
      const bis = i === 0 ? meta.heute : absteigend[i - 1]!.datum;
      const dauer = Math.round(
        (new Date(bis + 'T00:00:00').getTime() - new Date(h.datum + 'T00:00:00').getTime()) /
          86_400_000,
      );
      return {
        ...h,
        dauerText:
          dauer <= 0
            ? 'heute'
            : `${dauer} ${dauer === 1 ? 'Tag' : 'Tage'} ${i === 0 ? 'offen' : 'gedauert'}`,
        linie: i < absteigend.length - 1,
        aktuell: i === 0,
      };
    });
  }, [a, meta.heute]);

  if (abfrage.isLoading) {
    return <div className="eh-inhalt">Angebot wird geladen …</div>;
  }
  if (!a) {
    return (
      <div className="eh-inhalt">
        <div className="eh-karte">Dieses Angebot gibt es nicht (mehr).</div>
      </div>
    );
  }

  const erinnBadge = !meta.aktiveStatus.includes(a.status)
    ? `Kein Nachfassen – Status ${a.status}`
    : a.nachfass.antwortAm
      ? `Kundenantwort am ${dat(a.nachfass.antwortAm)} erkannt – kein Anruf nötig`
      : !a.nachfass.aktiv
        ? 'Nachfassen pausiert'
        : a.nachfass.mailVerfallen
          ? `Erinnerung entfällt – seit ${dat(a.nachfass.anrufAb)} ist der Anruf dran`
          : a.nachfass.erledigt < 1
            ? `1. Erinnerung geht automatisch am ${dat(a.nachfass.mailAm)} raus`
            : a.nachfass.anrufFaellig
              ? `Anruf ausstehend seit ${dat(a.nachfass.anrufAb)}`
              : `Anruf fällig am ${dat(a.nachfass.anrufAb)}`;

  const erinnTon = a.nachfass.antwortAm
    ? 'gruen'
    : a.nachfass.anrufFaellig
      ? 'rot'
      : 'neutral';

  /**
   * Versandstand.
   *
   * Maßgeblich ist die Historie, nicht das Statusfeld: Ein Vorgang kann
   * mehrfach hinausgegangen sein, und die Uhr zählt ab dem letzten Mal.
   */
  const versendet = a.versandHistorie.length > 0;
  const letzterVersand = a.versandHistorie.at(-1) ?? null;

  /**
   * Was von hier aus möglich ist.
   *
   * Der Status ist das Ergebnis einer Handlung, kein Feld, das man setzt.
   * Angeboten wird deshalb nur, was vom aktuellen Stand aus Sinn ergibt –
   * und jede Handlung bringt mit, was sie braucht: Versanddatum, Auftragshöhe,
   * Wiedervorlage.
   *
   * `ton` steuert die Farbe. Sie ist keine Verzierung: Rot ist der Schritt,
   * der das Angebot zum Kunden bringt, Grün sind die guten Ausgänge, Grau ist
   * Vorbereitung. Man soll auf einen Blick sehen, was gewichtig ist.
   */
  const schritte: { label: string; hinweis: string; ton: string; tun: () => void }[] = [];

  if (a.status === 'START') {
    schritte.push({
      label: 'Kalkulation begonnen',
      hinweis: 'Im Board sichtbar, dass jemand dran ist',
      ton: 'neutral',
      tun: () => statusMutation.mutate('in Arbeit'),
    });
  }

  if (['START', 'in Arbeit'].includes(a.status)) {
    schritte.push({
      label: 'Zur technischen Freigabe',
      hinweis: 'Liegt beim Techniker, bevor es hinausgeht',
      ton: 'neutral',
      tun: () => statusMutation.mutate('Freigabe Technik'),
    });
  }

  if (['START', 'in Arbeit', 'Freigabe Technik'].includes(a.status)) {
    schritte.push({
      label: 'Angebot versendet',
      hinweis: 'Erfasst Datum und Versender, startet die Nachfass-Uhr',
      ton: 'rot',
      tun: () => setVersendetOffen(true),
    });
  }

  if (['Gesendet', 'Wiedervorlage'].includes(a.status)) {
    schritte.push({
      label: 'Rückmeldung erfassen',
      hinweis: 'Notiz, Wiedervorlage, Absage – oder zurück in die Bearbeitung',
      ton: 'blau',
      tun: () => setAnrufOffen(true),
    });
  }

  if (a.status === 'Gesendet') {
    schritte.push({
      label: 'Auftrag erteilt',
      hinweis: 'Auftragshöhe wird abgefragt und bestätigt',
      ton: 'gruen',
      tun: () => setAuftragOffen(true),
    });
    schritte.push({
      label: 'Erneut versendet',
      hinweis: 'Nach Überarbeitung – die Nachfass-Uhr beginnt von vorn',
      ton: 'neutral',
      tun: () => setVersendetOffen(true),
    });
  }

  if (a.status === 'Auftrag erteilt') {
    schritte.push({
      label: 'Abgenommen',
      hinweis: 'Übergeben und vom Kunden angenommen',
      ton: 'gruen',
      tun: () => statusMutation.mutate('Abgenommen'),
    });
  }

  const erinnLog = a.erinnerungen
    .map(
      (e) =>
        `1. Erinnerung ${e.art === 'auto' ? 'automatisch versendet' : e.artText} am ${e.datumText} im Namen von ${e.user}` +
        (e.trockenlauf ? ' (Testbetrieb: nur als Entwurf abgelegt)' : ''),
    )
    .concat(
      a.nachfass.antwortAm
        ? [`Kundenantwort am ${dat(a.nachfass.antwortAm)} im Outlook-Postfach erkannt`]
        : [],
    )
    .join('  ·  ');

  const wertOffen = a.auftrag === null || a.auftrag === 0;
  const wertWarnung = a.status === 'Auftrag erteilt' && wertOffen;

  const auftragHinweis = wertWarnung
    ? 'Status ist Auftrag erteilt – bitte Auftragshöhe eintragen, der Wert fließt in die Gewinnprognose ein.'
    : a.status === 'Auftrag erteilt'
      ? 'Auftrag erteilt – der Wert zählt in die Gewinnprognose, bis der Vorgang abgenommen ist.'
      : versendet
        ? 'Kann vorgemerkt werden. Bei „Auftrag erteilt“ wird der Wert noch einmal vorgelegt und muss bestätigt werden.'
        : 'Kann vorgemerkt werden. Verbindlich wird der Wert erst mit „Auftrag erteilt“.';

  const datensatz = [
    { label: 'Laufende Nummer', wert: String(a.nr) },
    { label: 'Kategorie', wert: `${a.prefix} – ${a.kategorie}` },
    { label: 'Kalkulant', wert: a.kalkulant },
    { label: 'Kunde', wert: a.kunde.name },
    { label: 'Ort', wert: a.kunde.ort },
    { label: 'E-Mail', wert: a.kunde.mail || '—' },
    { label: 'Statuswechsel', wert: String(a.verlauf.length) },
    { label: 'Auftragshöhe', wert: eur(a.auftrag) },
  ];

  return (
    <div className="detail">
      <button type="button" className="detail-zurueck" onClick={() => navigate(-1)}>
        ← Zurück
      </button>

      <div className="detail-kopf">
        <div>
          <div className="detail-kategorie">
            <span className="detail-prefix">{a.prefix}</span>
            <span className="detail-kategorie-text">
              {a.kategorie} · angelegt {dat(a.angelegtAm)}
            </span>
          </div>
          <div className="detail-name">{a.name}</div>
          <div className="detail-kunde">
            {a.kunde.name} · {a.kunde.adresse}, {a.kunde.ort} · {a.kunde.telefon || '—'}
          </div>
        </div>

        <div className="detail-verknuepfungen">
          <VerknuepfungsKarte
            label="Monday-Item"
            text="Kommunikation & Verlauf ↗"
            url={a.mondayUrl}
            leerHinweis="Noch nicht verknüpft"
          />
          <VerknuepfungsKarte
            label="SharePoint"
            text="Angebotsordner ↗"
            url={a.sharepointUrl}
            leerHinweis="Noch nicht verknüpft"
          />
          {/*
            Der Explorer lässt sich aus dem Browser nur über ein eigenes
            Protokoll öffnen – ein file://-Link wird blockiert. Das Protokoll
            richtet werkzeuge/explorer/einrichten.cmd auf dem Rechner ein.
            Fehlt es, passiert beim Klick nichts; der SharePoint-Link daneben
            funktioniert immer. Deshalb steht er weiterhin da.
          */}
          <div className="detail-verknuepfung">
            <div className="detail-verknuepfung-label">Im Explorer</div>
            <a
              className="detail-verknuepfung-link"
              href={`kora://oeffne?art=${a.status === 'Auftrag erteilt' ? 'auftrag' : 'angebot'}&ordner=${encodeURIComponent(a.ordnerName)}`}
              title="Öffnet den synchronisierten Ordner im Windows-Explorer"
            >
              Ordner öffnen ↗
            </a>
          </div>
        </div>
      </div>

      <div className="detail-raster">
        <div className="detail-spalte">
          {/* ---- Statuskarte ---- */}
          <div className="detail-karte">
            <div className="detail-status-kopf">
              <div>
                <div className="detail-label">Aktueller Status</div>
                <div className="detail-status-zeile">
                  <span className="detail-status-punkt" style={{ background: a.statusPunkt }} />
                  <span
                    className="detail-status-text"
                    style={{
                      color: a.status === 'Auftrag erteilt' ? 'var(--eh-gruen)' : 'var(--eh-text)',
                    }}
                  >
                    {a.status}
                  </span>
                  <span
                    className="detail-alter"
                    style={{ background: a.ampel.bg, color: a.ampel.fg }}
                  >
                    {a.alterText} im Status
                  </span>
                </div>

                {(a.pausiert || a.wvFaellig) && (
                  <div className="detail-pause">
                    <span className="detail-pause-punkt" />
                    <span>
                      {a.pausiert
                        ? `Wiedervorlage bis ${dat(a.wiedervorlage)}`
                        : `Wiedervorlage fällig seit ${dat(a.wiedervorlage)}`}
                    </span>
                  </div>
                )}

                {a.status === 'Auftrag erteilt' && a.auftrag ? (
                  <div className="detail-auftragswert">
                    <span className="detail-auftragswert-label">Auftragswert</span>
                    <span className="detail-auftragswert-zahl">{a.auftragText}</span>
                    <span className="detail-auftragswert-note">netto, vor Steuern</span>
                  </div>
                ) : null}
              </div>

              <div className="detail-aktionen">
                <div className="detail-seit">
                  seit {a.statusDatumText}
                  <br />
                  durch {a.statusUser}
                </div>
              </div>
            </div>

            {a.wvKonflikt && (
              <div className="detail-warnung">
                <div className="detail-warnung-text">
                  <span className="detail-warnung-icon">!</span>
                  <div>
                    <div className="detail-warnung-titel">
                      In Monday pausiert, aber kein Wiedervorlage-Datum hinterlegt – bitte nachtragen
                    </div>
                    <div className="detail-warnung-unter">
                      Ohne Datum taucht das Angebot nie wieder von selbst auf.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="detail-warnung-knopf"
                  onClick={() => setAnrufOffen(true)}
                >
                  Anruf nachtragen
                </button>
              </div>
            )}

            {a.syncFehler && (
              <div className="detail-warnung">
                <div className="detail-warnung-text">
                  <span className="detail-warnung-icon">!</span>
                  <div>
                    <div className="detail-warnung-titel">
                      Status lokal gesetzt, Sync zu Monday/SharePoint fehlgeschlagen
                    </div>
                    <div className="detail-warnung-unter">{a.syncFehler}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="detail-warnung-knopf"
                  disabled={syncMutation.isPending}
                  onClick={() => syncMutation.mutate()}
                >
                  {syncMutation.isPending ? 'Läuft …' : 'Erneut versuchen'}
                </button>
              </div>
            )}

            {/* ---- Beteiligte ---- */}
            <div className="detail-beteiligte">
              <div className="detail-personen">
                <div className="detail-person">
                  <span className="eh-avatar detail-avatar">{a.kalkInitialen}</span>
                  <span className="detail-person-text">
                    <span className="detail-person-name">{a.kalkulant}</span>
                    {kalkWechsel ? (
                      <select
                        className="detail-kalk-auswahl"
                        value={a.kalkulant}
                        disabled={kalkulantMutation.isPending}
                        onChange={(e) => kalkulantMutation.mutate(e.target.value)}
                      >
                        {meta.team
                          .filter((t) => t.imVertrieb)
                          .map((t) => (
                            <option key={t.name} value={t.name}>
                              {t.name}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <span className="detail-person-rolle">
                        Kalkulant · fachlich verantwortlich
                        <button
                          type="button"
                          className="detail-kalk-knopf"
                          title="Zuständigkeit wechseln"
                          onClick={() => setKalkWechsel(true)}
                        >
                          wechseln
                        </button>
                      </span>
                    )}
                  </span>
                </div>

                {/*
                  Der Versender erscheint erst, wenn tatsächlich versendet wurde.
                  „Nicht ermittelt" bei einem Vorgang, der nie hinausging, ist
                  keine Auskunft, sondern Rauschen.
                */}
                {versendet && (
                  <div className="detail-person">
                    <span
                      className="eh-avatar detail-avatar"
                      style={
                        a.versAbweichend
                          ? { background: 'var(--eh-rot-flaeche)', color: 'var(--eh-rot)' }
                          : undefined
                      }
                    >
                      {a.versInitialen}
                    </span>
                    <span className="detail-person-text">
                      <span className="detail-person-name">{letzterVersand?.versender}</span>
                      <span className="detail-person-rolle">
                        Versender · Erinnerungen gehen in seinem Namen raus
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div>
                <span className={`detail-erinn-badge ${erinnTon}`}>{erinnBadge}</span>
              </div>
            </div>

            {erinnLog && <div className="detail-vers-text">{erinnLog}</div>}

            {/* ---- Versandhistorie ---- */}
            {versendet && (
              <div className="detail-abschnitt">
                <div className="detail-abschnitt-titel">
                  {a.versandHistorie.length === 1 ? 'Versendet' : 'Versandhistorie'}
                </div>
                <div className="detail-versandliste">
                  {a.versandHistorie.map((v) => (
                    <div
                      key={v.nummer}
                      className={`detail-versand ${v.nummer === a.versandHistorie.length ? 'aktuell' : ''}`}
                    >
                      <span className="detail-versand-nr">{v.nummer}.</span>
                      <span className="detail-versand-datum">{v.datumText}</span>
                      <span className="detail-versand-wer">
                        durch {v.versender}
                        {v.erfasstDurch && (
                          <span className="detail-versand-erfasst">
                            {' '}
                            · eingetragen von {v.erfasstDurch}
                          </span>
                        )}
                      </span>
                      {v.nummer === a.versandHistorie.length && a.versandHistorie.length > 1 && (
                        <span className="detail-versand-marke">Nachfass-Uhr läuft ab hier</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ---- Was als Nächstes ---- */}
            <div className="detail-abschnitt">
              <div className="detail-abschnitt-titel">Was als Nächstes</div>
              {schritte.length > 0 ? (
                <div className="detail-schritte">
                  {schritte.map((sch) => (
                    <button
                      key={sch.label}
                      type="button"
                      className={`detail-schritt ${sch.ton ?? ''}`}
                      disabled={statusMutation.isPending}
                      onClick={sch.tun}
                    >
                      <span className="detail-schritt-label">{sch.label}</span>
                      <span className="detail-schritt-hinweis">{sch.hinweis}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="detail-status-hinweis">
                  Dieser Vorgang ist abgeschlossen. Über „Status korrigieren“ lässt sich ein
                  Versehen richtigstellen.
                </div>
              )}

              {/*
                Ohne Chip-Reihe braucht es einen Weg zurück – aber als bewusste
                Handlung, die im Verlauf steht, nicht als Nebenwirkung eines
                Klicks auf das falsche Etikett.
              */}
              <div className="detail-neben">
                {a.ergaenzbareGewerke.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setGewerkOffen(true)}
                    title="Beschattung oder Insektenschutz unter derselben Nummer ergänzen"
                  >
                    Gewerk ergänzen
                  </button>
                )}
                <button type="button" onClick={() => setKorrekturOffen((o) => !o)}>
                  Status korrigieren
                </button>
              </div>

              {korrekturOffen && (
                <div className="detail-korrektur-feld">
                  <div className="detail-korrektur-text">
                    Nur für Versehen. Der Wechsel steht mit deinem Namen im Verlauf.
                  </div>
                  <div className="eh-chips">
                    {meta.status
                      .filter((x) => x !== 'Wiedervorlage' && x !== a.status)
                      .map((x) => (
                        <button
                          key={x}
                          type="button"
                          className="detail-status-chip"
                          disabled={statusMutation.isPending}
                          onClick={() => {
                            statusMutation.mutate(x);
                            setKorrekturOffen(false);
                          }}
                        >
                          <span
                            className="detail-chip-punkt"
                            style={{ background: meta.statusPunkt[x] ?? '#A89C92' }}
                          />
                          {x}
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>


            {/* ---- Auftragshöhe ---- */}
            <div className="detail-abschnitt">
              <div className={`detail-auftragsfeld ${wertWarnung ? 'warnung' : ''}`}>
                <div className="detail-auftragsfeld-zeile">
                  <label className="detail-auftrag-label">
                    <span>Auftragshöhe · netto, vor Steuern</span>
                    <span className="detail-auftrag-eingabe">
                      <input
                        value={
                          auftragFeld ?? (a.auftrag ? gruppiere(a.auftrag) : '')
                        }
                        placeholder="0"
                        onChange={(e) => setAuftragFeld(betragFormatieren(e.target.value))}
                        onBlur={() => {
                          if (auftragFeld === null) return;
                          wertMutation.mutate(betragLesen(auftragFeld));
                          setAuftragFeld(null);
                        }}
                      />
                      <span>€</span>
                    </span>
                  </label>

                </div>
                <div
                  className="detail-auftrag-hinweis"
                  style={{ color: wertWarnung ? 'var(--eh-rot)' : 'var(--eh-text-5)' }}
                >
                  {auftragHinweis}
                </div>
              </div>
            </div>
          </div>

          {/* ---- Mail-Verlauf ---- */}
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Mail-Verlauf</div>
              <div className="detail-anzahl">
                {a.erinnerungen.length === 1 ? '1 Mail' : `${a.erinnerungen.length} Mails`}
              </div>
            </div>
            <div className="detail-mailliste">
              {a.erinnerungen.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="detail-mailzeile"
                  onClick={() => setMailOffen(true)}
                >
                  <span className="detail-mail-art">{e.stufe}. Erinnerung</span>
                  <span className="detail-mail-zeit">
                    {e.wochentag}, {e.datumText} · {e.zeit || '—'}
                  </span>
                  <span className="detail-mail-user">{e.user}</span>
                  <span className="detail-mail-status">
                    <span className={`detail-mail-badge ${e.art === 'auto' ? 'gruen' : ''}`}>
                      {e.trockenlauf ? 'Entwurf im Testbetrieb' : e.artText}
                    </span>
                    <span className="detail-mail-chevron">›</span>
                  </span>
                </button>
              ))}
              {a.erinnerungen.length === 0 && (
                <div className="detail-leer">
                  {a.nachfass.mailVerfallen
                    ? 'Keine Erinnerungsmail versendet – dafür ist es inzwischen zu spät. ' +
                      'Kora holt sie nicht nach, weil eine automatische Nachfrage so lange nach ' +
                      'dem Angebot gedankenlos wirkt. Stattdessen steht der Anruf an.'
                    : 'Noch keine Erinnerungsmail zu diesem Angebot versendet.'}
                </div>
              )}
            </div>
          </div>

          {/* ---- Gesprächsnotizen ---- */}
          <div className="detail-karte">
            <div className="detail-karte-kopf">
              <div className="detail-label">Gesprächsnotizen</div>
              <div className="detail-anzahl">
                {a.anrufe.length === 1 ? '1 Anruf' : `${a.anrufe.length} Anrufe`}
              </div>
            </div>
            <div className="detail-anrufliste">
              {a.anrufe.map((c) => (
                <div
                  key={c.id}
                  className="detail-anruf"
                  style={{ borderLeftColor: c.farbe.akzent }}
                >
                  <div className="detail-anruf-kopf">
                    <span className="detail-anruf-zeit">
                      {c.wochentag}, {c.datumText} · {c.zeit}
                    </span>
                    <span className="detail-anruf-user">{c.user}</span>
                  </div>
                  <div className="detail-anruf-notiz">{c.notiz}</div>
                  <div className="detail-anruf-fuss">
                    <span
                      className="detail-anruf-badge"
                      style={{ background: c.farbe.bg, color: c.farbe.fg }}
                    >
                      {c.ergebnisLabel}
                    </span>
                    <span className="detail-anruf-zusatz">
                      {c.wiedervorlage ? `Wiedervorlage am ${dat(c.wiedervorlage)}` : c.sonst}
                    </span>
                  </div>
                </div>
              ))}
              {a.anrufe.length === 0 && (
                <div className="detail-leer">Noch kein Anruf dokumentiert.</div>
              )}
            </div>
          </div>

          {/* ---- Statusverlauf ---- */}
          <div className="detail-karte">
            <div className="detail-label" style={{ marginBottom: 18 }}>
              Statusverlauf
            </div>
            <div>
              {verlauf.map((h, i) => (
                <div className="detail-verlauf-zeile" key={`${h.datum}-${i}`}>
                  <div className="detail-verlauf-spur">
                    <span className="detail-verlauf-punkt" style={{ background: h.punkt }} />
                    <span
                      className="detail-verlauf-linie"
                      style={{ background: h.linie ? 'var(--eh-rahmen)' : 'transparent' }}
                    />
                  </div>
                  <div className="detail-verlauf-inhalt">
                    <div className="detail-verlauf-kopf">
                      <span className="detail-verlauf-status">{h.status}</span>
                      <span className="detail-verlauf-datum">{h.datumText}</span>
                      <span className="detail-verlauf-dauer">{h.dauerText}</span>
                    </div>
                    <div className="detail-verlauf-notiz">{h.notiz || '—'}</div>
                    <div className="detail-verlauf-user">
                      {h.user}
                      {h.aktuell ? ' · aktuell' : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- Rechte Spalte ---- */}
        <div className="detail-seitenspalte">
          <div className="detail-karte detail-datensatz">
            <div className="detail-label">Datensatz</div>
            {datensatz.map((m) => (
              <div className="detail-datenzeile" key={m.label}>
                <span className="detail-datenlabel">{m.label}</span>
                <span className="detail-datenwert">{m.wert}</span>
              </div>
            ))}
          </div>

          <div className="detail-karte detail-geschwister">
            <div className="detail-label" style={{ marginBottom: 13 }}>
              Weitere Angebote des Kunden
            </div>
            <div className="detail-geschwister-liste">
              {geschwister.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className="detail-geschwister-knopf"
                  onClick={() => navigate(`/angebot/${g.id}`)}
                >
                  <span className="detail-geschwister-name">{g.name}</span>
                  <span className="detail-geschwister-meta">
                    {g.status} · {g.alterText}
                  </span>
                </button>
              ))}
              {geschwister.length === 0 && (
                <div className="detail-leer">Einzelangebot — keine weiteren Vorgänge.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {gewerkOffen && (
        <GewerkModal
          angebot={a}
          onSchliessen={() => setGewerkOffen(false)}
          onFertig={(neueId) => {
            aktualisieren();
            navigate(`/angebot/${neueId}`);
          }}
        />
      )}
      {versendetOffen && (
        <VersendetModal
          angebot={a}
          onSchliessen={() => setVersendetOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {auftragOffen && (
        <AuftragModal
          angebot={a}
          onSchliessen={() => setAuftragOffen(false)}
          onFertig={aktualisieren}
        />
      )}
      {anrufOffen && (
        <AnrufModal angebot={a} onSchliessen={() => setAnrufOffen(false)} onFertig={aktualisieren} />
      )}
      {mailOffen && <MailVorschauModal angebot={a} onSchliessen={() => setMailOffen(false)} />}
    </div>
  );
}

function VerknuepfungsKarte({
  label,
  text,
  url,
  leerHinweis,
}: {
  label: string;
  text: string;
  url: string | null;
  leerHinweis: string;
}) {
  if (!url) {
    return (
      <div className="detail-verknuepfung detail-verknuepfung--leer">
        <span className="detail-verknuepfung-label">{label}</span>
        <span className="detail-verknuepfung-text">{leerHinweis}</span>
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="detail-verknuepfung">
      <span className="detail-verknuepfung-label">{label}</span>
      <span className="detail-verknuepfung-text">{text}</span>
    </a>
  );
}
